import { alpha, createTheme, lighten, darken } from '@mui/material';
import type { Shadows } from '@mui/material/styles';
import { ptBR } from '@mui/material/locale';
import '@mui/lab/themeAugmentation';

// Minimal UI Kit / EquipeDeLouvor palette (from config: #1E70A1, #2C3C4F)
const themeColors = {
  primary: '#1E70A1',
  secondary: '#2C3C4F',
  success: '#44D600',
  warning: '#FFA319',
  error: '#d70027',
  info: '#33C2FF',
  black: '#212B36',
  white: '#ffffff',
  primaryAlt: '#0d3d5a',
};

const colors = {
  gradients: {
    blue1: 'linear-gradient(135deg, #1E70A1 0%, #0d3d5a 100%)',
    hero: 'linear-gradient(135deg, #0d3d5a 0%, #1E70A1 50%, #2C3C4F 100%)',
    blue2: 'linear-gradient(135deg, #ABDCFF 0%, #0396FF 100%)',
    blue3: 'linear-gradient(127.55deg, #141E30 3.73%, #243B55 92.26%)',
    orange1: 'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)',
    orange2: 'linear-gradient(135deg, #FFD3A5 0%, #FD6585 100%)',
    purple1: 'linear-gradient(135deg, #43CBFF 0%, #9708CC 100%)',
    pink1: 'linear-gradient(135deg, #F6CEEC 0%, #D939CD 100%)',
    pink2: 'linear-gradient(135deg, #F761A1 0%, #8C1BAB 100%)',
    green1: 'linear-gradient(135deg, #FFF720 0%, #3CD500 100%)',
    black1: 'linear-gradient(100.66deg, #434343 6.56%, #000000 93.57%)',
  },
  shadows: {
    success:
      '0px 1px 4px rgba(68, 214, 0, 0.25), 0px 3px 12px 2px rgba(68, 214, 0, 0.35)',
    error:
      '0px 1px 4px rgba(255, 25, 67, 0.25), 0px 3px 12px 2px rgba(255, 25, 67, 0.35)',
    info:
      '0px 1px 4px rgba(51, 194, 255, 0.25), 0px 3px 12px 2px rgba(51, 194, 255, 0.35)',
    primary:
      '0px 1px 4px rgba(30, 112, 161, 0.25), 0px 3px 12px 2px rgba(30, 112, 161, 0.35)',
    warning:
      '0px 1px 4px rgba(255, 163, 25, 0.25), 0px 3px 12px 2px rgba(255, 163, 25, 0.35)',
    card:
      '0px 0px 2px rgba(33, 43, 54, 0.08), 0px 2px 8px rgba(33, 43, 54, 0.08)',
    cardSm:
      '0px 0px 1px rgba(33, 43, 54, 0.06), 0px 1px 4px rgba(33, 43, 54, 0.06)',
    cardLg:
      '0 8px 24px rgba(33, 43, 54, 0.12), 0 2px 8px rgba(33, 43, 54, 0.08)',
  },
  layout: {
    general: {
      bodyBg: '#F9FAFB',
    },
    sidebar: {
      background: themeColors.white,
      textColor: themeColors.secondary,
      dividerBg: '#F3F4F6',
      menuItemColor: '#566573',
      menuItemColorActive: themeColors.primary,
      menuItemBg: 'transparent',
      menuItemBgActive: alpha(themeColors.primary, 0.08),
      menuItemIconColor: themeColors.secondary,
      menuItemIconColorActive: themeColors.primary,
      menuItemHeadingColor: darken(themeColors.secondary, 0.2),
    },
  },
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white,
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white,
    },
    black: {
      5: alpha(themeColors.black, 0.02),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black,
    },
  },
  secondary: {
    lighter: lighten(themeColors.secondary, 0.85),
    light: lighten(themeColors.secondary, 0.25),
    main: themeColors.secondary,
    dark: darken(themeColors.secondary, 0.2),
  },
  primary: {
    lighter: alpha(themeColors.primary, 0.12),
    light: lighten(themeColors.primary, 0.3),
    main: themeColors.primary,
    dark: darken(themeColors.primary, 0.2),
  },
  success: {
    lighter: lighten(themeColors.success, 0.85),
    light: lighten(themeColors.success, 0.3),
    main: themeColors.success,
    dark: darken(themeColors.success, 0.2),
  },
  warning: {
    lighter: lighten(themeColors.warning, 0.85),
    light: lighten(themeColors.warning, 0.3),
    main: themeColors.warning,
    dark: darken(themeColors.warning, 0.2),
  },
  error: {
    lighter: lighten(themeColors.error, 0.85),
    light: lighten(themeColors.error, 0.3),
    main: themeColors.error,
    dark: darken(themeColors.error, 0.2),
  },
  info: {
    lighter: lighten(themeColors.info, 0.85),
    light: lighten(themeColors.info, 0.3),
    main: themeColors.info,
    dark: darken(themeColors.info, 0.2),
  },
};

const subtleShadows = [
  'none',
  colors.shadows.cardSm,
  colors.shadows.card,
  '0px 4px 12px rgba(33, 43, 54, 0.08)',
  '0px 6px 16px rgba(33, 43, 54, 0.1)',
  '0px 8px 20px rgba(33, 43, 54, 0.1)',
  '0px 10px 24px rgba(33, 43, 54, 0.12)',
  '0px 12px 28px rgba(33, 43, 54, 0.12)',
  '0px 14px 32px rgba(33, 43, 54, 0.14)',
  '0px 16px 36px rgba(33, 43, 54, 0.14)',
  '0px 18px 40px rgba(33, 43, 54, 0.16)',
  '0px 20px 44px rgba(33, 43, 54, 0.16)',
  '0px 22px 48px rgba(33, 43, 54, 0.18)',
  '0px 24px 52px rgba(33, 43, 54, 0.18)',
  '0px 26px 56px rgba(33, 43, 54, 0.2)',
  '0px 28px 60px rgba(33, 43, 54, 0.2)',
  '0px 30px 64px rgba(33, 43, 54, 0.22)',
  '0px 32px 68px rgba(33, 43, 54, 0.22)',
  '0px 34px 72px rgba(33, 43, 54, 0.24)',
  '0px 36px 76px rgba(33, 43, 54, 0.24)',
  '0px 38px 80px rgba(33, 43, 54, 0.26)',
  '0px 40px 84px rgba(33, 43, 54, 0.26)',
  '0px 42px 88px rgba(33, 43, 54, 0.28)',
  '0px 44px 92px rgba(33, 43, 54, 0.28)',
  '0px 46px 96px rgba(33, 43, 54, 0.3)',
] as Shadows;

export const MinimalLightTheme = createTheme({
  colors: {
    gradients: colors.gradients,
    shadows: {
      success: colors.shadows.success,
      error: colors.shadows.error,
      primary: colors.shadows.primary,
      warning: colors.shadows.warning,
    },
    alpha: {
      white: colors.alpha.white,
      trueWhite: colors.alpha.trueWhite,
      black: colors.alpha.black,
    },
    secondary: {
      lighter: alpha(themeColors.secondary, 0.1),
      light: lighten(themeColors.secondary, 0.3),
      main: themeColors.secondary,
      dark: darken(themeColors.secondary, 0.2),
    },
    primary: {
      lighter: colors.primary.lighter,
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark,
    },
    success: {
      lighter: alpha(themeColors.success, 0.1),
      light: lighten(themeColors.success, 0.3),
      main: themeColors.success,
      dark: darken(themeColors.success, 0.2),
    },
    warning: {
      lighter: alpha(themeColors.warning, 0.1),
      light: lighten(themeColors.warning, 0.3),
      main: themeColors.warning,
      dark: darken(themeColors.warning, 0.2),
    },
    error: {
      lighter: alpha(themeColors.error, 0.1),
      light: lighten(themeColors.error, 0.3),
      main: themeColors.error,
      dark: darken(themeColors.error, 0.2),
    },
    info: {
      lighter: alpha(themeColors.info, 0.1),
      light: lighten(themeColors.info, 0.3),
      main: themeColors.info,
      dark: darken(themeColors.info, 0.2),
    },
  },
  general: {
    reactFrameworkColor: '#00D8FF',
    borderRadiusSm: '6px',
    borderRadius: '8px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px',
  },
  sidebar: {
    background: colors.layout.sidebar.background,
    textColor: colors.layout.sidebar.textColor,
    dividerBg: colors.layout.sidebar.dividerBg,
    menuItemColor: colors.layout.sidebar.menuItemColor,
    menuItemColorActive: colors.layout.sidebar.menuItemColorActive,
    menuItemBg: colors.layout.sidebar.menuItemBg,
    menuItemBgActive: colors.layout.sidebar.menuItemBgActive,
    menuItemIconColor: colors.layout.sidebar.menuItemIconColor,
    menuItemIconColorActive: colors.layout.sidebar.menuItemIconColorActive,
    menuItemHeadingColor: colors.layout.sidebar.menuItemHeadingColor,
    boxShadow: colors.shadows.cardSm,
    width: '260px',
  },
  header: {
    height: '64px',
    background: colors.alpha.white[100],
    boxShadow: colors.shadows.cardSm,
    textColor: themeColors.secondary,
  },
  spacing: 8,
  palette: {
    common: {
      black: colors.alpha.black[100],
      white: colors.alpha.white[100],
    },
    mode: 'light',
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark,
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: colors.secondary.dark,
    },
    error: {
      light: colors.error.light,
      main: colors.error.main,
      dark: colors.error.dark,
      contrastText: colors.alpha.white[100],
    },
    success: {
      light: colors.success.light,
      main: colors.success.main,
      dark: colors.success.dark,
      contrastText: colors.alpha.white[100],
    },
    info: {
      light: colors.info.light,
      main: colors.info.main,
      dark: colors.info.dark,
      contrastText: colors.alpha.white[100],
    },
    warning: {
      light: colors.warning.light,
      main: colors.warning.main,
      dark: colors.warning.dark,
      contrastText: colors.alpha.white[100],
    },
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
      A100: '#f3f4f6',
      A200: '#e5e7eb',
      A400: '#9ca3af',
      A700: '#6b7280',
    },
    text: {
      primary: colors.alpha.black[100],
      secondary: colors.alpha.black[70],
      disabled: colors.alpha.black[50],
    },
    background: {
      paper: colors.alpha.white[100],
      default: colors.layout.general.bodyBg,
    },
    action: {
      active: colors.alpha.black[100],
      hover: colors.primary.lighter,
      hoverOpacity: 0.08,
      selected: colors.alpha.black[10],
      selectedOpacity: 0.08,
      disabled: colors.alpha.black[50],
      disabledBackground: colors.alpha.black[5],
      disabledOpacity: 0.38,
      focus: colors.alpha.black[10],
      focusOpacity: 0.05,
      activatedOpacity: 0.12,
    },
    tonalOffset: 0.5,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1840,
    },
  },
  components: {
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(themeColors.primaryAlt, 0.2),
          backdropFilter: 'blur(2px)',
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'blur(2px)',
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#000000',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          width: '100%',
          height: '100%',
        },
        '#nprogress .bar': {
          background: colors.primary.main,
        },
        '#nprogress .spinner-icon': {
          borderTopColor: colors.primary.main,
          borderLeftColor: colors.primary.main,
        },
        '#nprogress .peg': {
          boxShadow: `0 0 10px ${colors.primary.main}, 0 0 5px ${colors.primary.main}`,
        },
        ':root': {
          '--swiper-theme-color': colors.primary.main,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: 0,
          border: `1px solid ${colors.alpha.black[10]}`,
          borderRadius: 12,
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: colors.shadows.cardSm,
        },
        elevation2: {
          boxShadow: colors.shadows.card,
        },
        elevation24: {
          boxShadow: colors.shadows.cardLg,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: colors.shadows.cardSm,
          border: `1px solid ${colors.alpha.black[5]}`,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'none',
          paddingLeft: 16,
          paddingRight: 16,
          borderRadius: 8,
        },
        endIcon: {
          marginRight: -8,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.alpha.black[50],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
            borderWidth: 1,
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            borderRadius: 8,
          },
          '&.MuiButtonBase-root.Mui-selected': {
            backgroundColor: colors.layout.sidebar.menuItemBgActive,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: colors.shadows.card,
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Work Sans", "Public Sans", "Archivo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 14,
    htmlFontSize: 16,
    h1: {
      fontWeight: 700,
      fontSize: 28,
    },
    h2: {
      fontWeight: 700,
      fontSize: 24,
    },
    h3: {
      fontWeight: 700,
      fontSize: 20,
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 700,
      fontSize: 18,
    },
    h5: {
      fontWeight: 600,
      fontSize: 16,
    },
    h6: {
      fontWeight: 600,
      fontSize: 15,
    },
    body1: {
      fontSize: 14,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: 14,
      lineHeight: 1.5,
    },
    button: {
      fontSize: 14,
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: 14,
      color: colors.alpha.black[70],
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: 13,
      color: colors.alpha.black[70],
    },
  },
  shadows: subtleShadows,
}, ptBR);
